/**
 * L-M4 C1: keeps a material's colour graph out of the shadow passes, with zero look change.
 *
 * three 0.186.1 `Renderer._getShadowNodes` multiplies the shadow alpha by `material.colorNode.a` whenever a material
 * has a `colorNode`. On a vec3 albedo `.a` is the constant 1.0 (`vec4(albedo, 1.0).w`), yet the whole albedo graph is
 * built into the shadow pipeline and its per-object uniforms (livery rows, engine rows, hits) are updated and compared
 * on every caster render of every shadow level (D-910, D-922: 42 OBJECT update nodes on High against 3).
 *
 * `litOnly` wraps the colour: every non-shadow build (the lit pass, any other override) gets the real graph; a build
 * whose material is three's shadow override (`isShadowPassMaterial`) gets a constant black vec3, whose `.a` is the
 * same constant 1.0. The shadow output stays `vec4(0, 0, 0, 1)` either way. Only for vec3 colours: a vec4 colour with
 * a real alpha would lose its alpha-tested shadow.
 */
import { Node, type NodeBuilder } from 'three/webgpu'
import { vec3 } from 'three/tsl'

type V3 = Node<'vec3'>

class LitOnlyColorNode extends Node<'vec3'> {
  static get type(): string {
    return 'LitOnlyColorNode'
  }

  readonly litNode: V3
  readonly shadowNode: V3

  constructor(litNode: V3, shadowNode: V3) {
    super('vec3')
    this.litNode = litNode
    this.shadowNode = shadowNode
  }

  override setup(builder: NodeBuilder): Node {
    const m = builder.material as { isShadowPassMaterial?: boolean } | null
    return m?.isShadowPassMaterial === true ? this.shadowNode : this.litNode
  }
}

/** `color` in every lit build; a constant in shadow builds (same `.a`, so value-identical). */
export function litOnly(color: V3): V3 {
  return new LitOnlyColorNode(color, vec3(0, 0, 0)) as unknown as V3
}
